import { useCallback, useState } from 'react'
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { Button, Card, Divider } from '@/ui/components'
// Importing directly from the components folder to avoid circular dependencies in the manual bundle chunking process
import { GoogleSignInButton } from '@/ui/components/molecules/GoogleAuthButton/GoogleAuthButton'
import { Routes } from 'routes'
import { CreateAccountForm } from './CreateAccountForm'
import { SignInForm } from './SignInForm'
import styled from 'styled-components'
import { useMutation } from '@tanstack/react-query'
import { SignupInput, VerifyInput } from '@/ui/services/AuthService'
import { VerifyForm } from './VerifyForm'
import { getServices } from '@/ui/core/services'
import { useAuthenticationState } from '@/ui/core'
import { FormattedMessage } from 'react-intl'
import { strings } from './Authentication.strings'

const FormCard = styled(Card)`
  gap: 20px;
  min-width: 350px;
  max-width: 450px;
`

export const Authentication: React.FC = () => {
  const [search] = useSearchParams()
  const authType = search.get('type')
  const destination = search.get('dest')
  const [username, setUsername] = useState<string | undefined>()
  const { auth, deviceStore } = getServices()
  const navigate = useNavigate()
  const { signIn, signUp, verify, loadingAuthState } = useAuthenticationState()

  const signupMutation = useMutation({
    mutationFn: async (input: SignupInput) => {
      const step = await signUp(input)
      switch (step) {
        case 'verify':
          setUsername(input.username)
          void navigate({
            pathname: Routes.authentication,
            search: destination
              ? createSearchParams({
                  type: 'verify',
                  dest: destination,
                }).toString()
              : createSearchParams({ type: 'verify' }).toString(),
          })
          break
        case 'done':
          if (destination) {
            void navigate(`${Routes.welcomeEvent}${destination}`)
            break
          }
          void navigate(Routes.dashboard)
          break
      }
    },
  })

  const signinMutation = useMutation({
    mutationFn: async (input: { username: string; password: string }) => {
      const step = await signIn(input)
      switch (step) {
        case 'verify':
          setUsername(input.username)
          void navigate(
            `${Routes.authentication}?type=verify${destination ? `&dest=${destination}` : ''}`,
          )
          break
        case 'done':
          if (destination) {
            void navigate(`${Routes.welcomeEvent}${destination}`)
            break
          }
          void navigate(Routes.dashboard)
          break
      }
    },
  })

  const verifyMutation = useMutation({
    mutationFn: async (input: VerifyInput) => {
      await verify(input)
      if (destination) {
        void navigate(`${Routes.welcomeEvent}${destination}`)
        return
      }
      void navigate(Routes.dashboard)
    },
  })

  const switchAuth = useCallback(
    (authType: 'signin' | 'create-account' | 'verify') => {
      void navigate(
        `${Routes.authentication}?type=${authType}${destination ? `&dest=${destination}` : ''}`,
      )
    },
    [navigate, destination],
  )

  const setDestination = useCallback(() => {
    if (destination) {
      deviceStore.setItem('destination', destination)
    }
  }, [destination, deviceStore])

  // TOOD: Will want to update the form or something if username is missing
  const resend = useCallback(() => {
    void auth.resendVerification(username ?? '')
  }, [auth, username])

  return (
    <FormCard>
      {authType === 'create-account' ? (
        <CreateAccountForm
          createAccount={(input) => signupMutation.mutate(input)}
          loading={signupMutation.isPending || loadingAuthState}
          error={signupMutation.error}
        />
      ) : authType === 'verify' ? (
        <VerifyForm
          verify={({ code }) =>
            verifyMutation.mutate({ username: username ?? '', code })
          }
          resend={resend}
          loading={verifyMutation.isPending || loadingAuthState}
          error={verifyMutation.error}
        />
      ) : (
        <SignInForm
          signIn={({ email, password }) =>
            signinMutation.mutate({ username: email, password })
          }
          loading={signinMutation.isPending || loadingAuthState}
          error={signinMutation.error}
        />
      )}
      {authType !== 'verify' && (
        <>
          <Divider />
          <GoogleSignInButton callback={setDestination} />
          <Button
            kind="secondary"
            onClick={() => {
              switchAuth(
                authType === 'create-account' ? 'signin' : 'create-account',
              )
            }}
          >
            {authType === 'create-account' ? (
              <FormattedMessage {...strings.signInBtn} />
            ) : (
              <FormattedMessage {...strings.createAccountBtn} />
            )}
          </Button>
        </>
      )}
    </FormCard>
  )
}
